/**
 * Intelligence Service — CMI API client
 *
 * Proxies all intelligence-zone requests through governance-service.
 * Path: /v1/governance/intelligence/hosts/{hostId}/...
 */

import { apiClient } from '@/api';
import { capsuleSchemaFromDetail } from './capsule-schema';
import { legacyCapsuleSurfacesEnabled } from '@/shared/api/intelligence-retirement';
import type { GetRequestOptions } from '@/api/client';
import type {
  CapsuleListItem,
  CapsuleDetail,
  CreateCapsuleRequest,
  CreateCapsuleResponse,
  DeclaredCapsuleDefinition,
  UpdateCapsuleRequest,
  CapsuleEnrichmentResponse,
  CapsuleStreamStatusesResponse,
  CapsuleStream,
  CapsuleSource,
  RegisterSourceRequest,
  UpdateSourceStreamsRequest,
  UpdateSourceStreamsResponse,
  QualityOverview,
  CapsuleQualityDetail,
  StaleDocument,
  DocumentSummary,
  DocumentDetail,
  TaxonomyNode,
  CreateTermRequest,
  PreviewResponse,
  AnnotationResponse,
  OverrideResponse,
  AssemblySummary,
  AssemblyTrace,
  VersionEntry,
  IntelligenceVersion,
  GraphTraverseResponse,
  IngestRequest,
  IngestResponse,
  EmbedResponse,
  UniversalBindingSearchParams,
  UniversalBindingSearchResponse,
  AuditEventsResponse,
  AuditEventsQuery,
  AuditTimelineResponse,
  AuditTimelineQuery,
  AgentOverlayPublishedEvidence,
  CreateOverlayWriteAttemptJournalRequest,
  CreateOverlayWriteAttemptJournalResponse,
  AgentOverlayDecisionOverrideRequest,
  AgentOverlayDecisionOverrideResponse,
  RefreshEstimateResponse,
  RefreshResponse,
} from '@/types/intelligence.types';

class IntelligenceService {
  private base(hostId: string) {
    return `governance/intelligence/hosts/${hostId}`;
  }

  // --- Health ---

  getVersion(hostId: string) {
    return apiClient.get<IntelligenceVersion>(`${this.base(hostId)}/version`);
  }

  // --- Capsules ---

  listCapsules(hostId: string, options?: GetRequestOptions) {
    return apiClient.get<CapsuleListItem[]>(`${this.base(hostId)}/capsules`, undefined, options);
  }

  getCapsule(hostId: string, id: string) {
    return apiClient.get<CapsuleDetail>(`${this.base(hostId)}/capsules/${id}`);
  }

  createCapsule(hostId: string, data: CreateCapsuleRequest) {
    return apiClient.post<CreateCapsuleResponse>(`${this.base(hostId)}/capsules`, data);
  }

  updateCapsule(hostId: string, id: string, data: UpdateCapsuleRequest) {
    return apiClient.put<CapsuleDetail>(`${this.base(hostId)}/capsules/${id}`, data);
  }

  defineDeclaredCapsule(hostId: string, id: string, data: DeclaredCapsuleDefinition) {
    return apiClient.post<CreateCapsuleResponse>(`${this.base(hostId)}/capsules/${id}/definition`, data);
  }

  transitionStatus(hostId: string, id: string, targetStatus: string) {
    return apiClient.put<{ id: string; status: string }>(
      `${this.base(hostId)}/capsules/${id}/status`,
      { targetStatus }
    );
  }

  listVersions(hostId: string, id: string) {
    return apiClient.get<VersionEntry[]>(`${this.base(hostId)}/capsules/${id}/versions`);
  }

  bumpVersion(hostId: string, id: string, newVersion: string) {
    return apiClient.post<{ versionId: string; newVersion: string }>(
      `${this.base(hostId)}/capsules/${id}/versions`,
      { newVersion }
    );
  }

  rollback(hostId: string, id: string, targetVersion: string) {
    return apiClient.post<{ rolledBackTo: string }>(
      `${this.base(hostId)}/capsules/${id}/rollback`,
      { targetVersion }
    );
  }

  deleteCapsule(hostId: string, id: string) {
    return apiClient.delete<{ status: string; message: string }>(
      `${this.base(hostId)}/capsules/${id}`
    );
  }

  triggerSync(
    hostId: string,
    id: string,
    mode: 'incremental' | 'full_refresh' = 'incremental',
    streamName?: string
  ) {
    const body: { mode: 'incremental' | 'full_refresh'; stream_name?: string } = { mode };
    if (streamName) {
      body.stream_name = streamName;
    }
    return apiClient.post<{ status: string; message: string }>(
      `${this.base(hostId)}/capsules/${id}/sync`,
      body
    );
  }

  /**
   * Directly fire extract-facts on the edge ingestion service. Fires
   * document facts and the entity-index/source-record identity projection.
   * The old `ingestion_row_facts_*` events remain audit-only legacy
   * compatibility and no longer back an active enrichment table row.
   */
  triggerExtractFacts(hostId: string, id: string) {
    return apiClient.post<{ status: string }>(
      `${this.base(hostId)}/capsules/${id}/extract-facts`,
      {}
    );
  }

  /**
   * Force-runs the classifier gate for every stream the capsule has
   * in sync_state. When `force` is true (default: the UI only invokes
   * this path explicitly from the "Refresh enrichment" button) the
   * edge gate bypasses its idempotency check and re-classifies even
   * when a prior classification_run event + decisions row exist —
   * required to unblock capsules stuck with partial-state bootstrap
   * decisions from before the sync-gate shipped (2026-04-21). For
   * auto/passive refresh flows pass `force=false` to preserve the
   * fast-path.
   */
  triggerReclassify(hostId: string, id: string, force = true) {
    return apiClient.post<{ status: string }>(`${this.base(hostId)}/capsules/${id}/reclassify`, {
      force,
    });
  }

  /**
   * Fires the F1/F2/F3 post-sync jobs (graph_edges, community_reports,
   * contextual_chunks) via intelligence-zone. Fire-and-forget: each
   * job runs on its own tokio task and emits its own audit triplet.
   */
  triggerEnrichCascade(hostId: string, id: string, body: Record<string, unknown> = {}) {
    return apiClient.post<{
      capsuleId: string;
      triggered: string[];
      skipped: string[];
    }>(`${this.base(hostId)}/capsules/${id}/enrich`, body);
  }

  updateAdvancedEnrichmentLayer(
    hostId: string,
    id: string,
    layer: 'community_reports' | 'hypothetical_questions',
    enabled: boolean
  ) {
    const body =
      layer === 'community_reports'
        ? { f2_community_reports: enabled }
        : { hypothetical_questions: enabled, f4_structured: enabled };
    return this.triggerEnrichCascade(hostId, id, body);
  }

  /**
   * Single "Refresh enrichment" action that fans out to reclassify +
   * extract-facts + enrich in parallel. Returns the three promises'
   * Promise.allSettled result so callers can surface partial failures
   * (e.g. F2/F3 skip with no_mcp_url) without aborting the whole
   * refresh. Each call is idempotent on the edge.
   */
  refreshEnrichment(hostId: string, id: string) {
    return Promise.allSettled([
      this.triggerReclassify(hostId, id),
      this.triggerExtractFacts(hostId, id),
      this.triggerEnrichCascade(hostId, id),
    ]);
  }

  getDependencies(hostId: string, id: string) {
    return apiClient.get<{ capsuleId: string; dependsOn: string[] }[]>(
      `${this.base(hostId)}/capsules/${id}/dependencies`
    );
  }

  // --- Sources ---

  registerSource(hostId: string, capsuleId: string, data: RegisterSourceRequest) {
    return apiClient.post<{ capsuleId: string; sourceType: string; streamsInitialized: number }>(
      `${this.base(hostId)}/capsules/${capsuleId}/sources`,
      data
    );
  }

  /**
   * List registered sources for a capsule, with derived active_schemas
   * and a credential-stripped config_snapshot. Used by the
   * CapsuleSettingsTab Sources card (plan §12.4) and the "Add schemas"
   * modal to build its existing-schema filter.
   */
  listCapsuleSources(hostId: string, capsuleId: string) {
    return apiClient.get<CapsuleSource[]>(`${this.base(hostId)}/capsules/${capsuleId}/sources`);
  }

  /**
   * Per-capsule enrichment rollup derived from capsule_audit_events.
   * Returns state (complete/running/failed/pending) + counts for each
   * active enrichment stage. V2 retired row_facts and renamed the
   * identity-facts projection to entity_index. V217 added the F4
   * structured-extraction triplet (aggregate_facts,
   * hypothetical_questions, schema_embeddings) and a top-level
   * `postSyncFailure` field that summarises
   * `ingestion_post_sync_step_failed` events so the UI can flag
   * capsules whose enrichment never started.
   */
  getCapsuleEnrichment(hostId: string, capsuleId: string) {
    return apiClient.get<CapsuleEnrichmentResponse>(
      `${this.base(hostId)}/capsules/${capsuleId}/enrichment`
    );
  }

  getCapsuleStreamStatuses(hostId: string, capsuleId: string) {
    return apiClient.get<CapsuleStreamStatusesResponse>(
      `${this.base(hostId)}/capsules/${capsuleId}/streams/status`
    );
  }

  listDocumentArtifacts(hostId: string, capsuleId: string) {
    return apiClient.get<DocumentArtifactListResponse>(
      `${this.base(hostId)}/capsules/${encodeURIComponent(capsuleId)}/document-artifacts`
    );
  }

  /**
   * One page of the capsule's documents that were skipped or failed, newest
   * first, with a reason code each. Served by the ingestion worker; a host
   * whose worker predates the route answers 404.
   */
  listNotIngestedDocuments(hostId: string, capsuleId: string, page: { limit: number; cursor?: string }) {
    const params: Record<string, string | number> = { limit: page.limit };
    if (page.cursor) params.cursor = page.cursor;
    return apiClient.get<NotIngestedDocumentsPage>(
      `${this.base(hostId)}/capsules/${encodeURIComponent(capsuleId)}/document-artifacts/not-ingested`,
      params,
      { retry: 0 }
    );
  }

  retryDocumentArtifact(hostId: string, capsuleId: string, artifactId: string) {
    return apiClient.post<DocumentArtifactTriggerResponse>(
      `${this.base(hostId)}/capsules/${encodeURIComponent(capsuleId)}/document-artifacts/${encodeURIComponent(artifactId)}/retry`,
      {}
    );
  }

  /**
   * Gives each SharePoint file that stopped after repeated read timeouts one
   * more try, on the next read of the capsule (administrators only). The
   * worker answers 202 with the files granted, or 200 with granted 0 when no
   * file was stopped; a host whose worker predates the route answers 404
   * (timed_out_retry_host_unsupported).
   */
  retryTimedOutDocuments(hostId: string, capsuleId: string) {
    return apiClient.post<TimedOutRetryResponse>(
      `${this.base(hostId)}/capsules/${encodeURIComponent(capsuleId)}/document-artifacts/not-ingested/timed-out/retry`,
      {}
    );
  }

  /**
   * Lists all ingested streams (tables) for a capsule.
   *
   * E4-S2: Replaces the source_type === 'postgres' gate for showing the
   * table view. Returns [{streamName, rowCount, lastIngestedAt}] for any
   * source type that populates capsule_data tables (Postgres, Shopify,
   * etc.). Doc-only capsules return an empty array, not an error.
   */
  getCapsuleStreams(hostId: string, capsuleId: string) {
    return apiClient.get<CapsuleStream[]>(`${this.base(hostId)}/capsules/${capsuleId}/streams`);
  }

  retryCapsuleStream(
    hostId: string,
    capsuleId: string,
    streamName: string,
    options?: { forceFullRefresh?: boolean }
  ) {
    return apiClient.post<{
      status: string;
      capsuleId: string;
      streamName: string;
      forceFullRefresh?: boolean;
      /** The read request the worker queued (manualTriggerResponse.trigger_id). */
      triggerId?: string;
      /** When the worker accepted the request. */
      acceptedAt?: string;
    }>(
      `${this.base(hostId)}/capsules/${capsuleId}/streams/${encodeURIComponent(streamName)}/retry`,
      options?.forceFullRefresh ? { force_full_refresh: true } : {}
    );
  }

  /**
   * Clears a stream's park and makes it due now, without a manual trigger.
   * For un-parking several streams at once: the host keeps one waiting
   * manual trigger per capsule, so a retry per stream would leave only the
   * last one reading now (challenge 2026-09-26-governance-stream-retry-forces-a-read).
   * Use retryCapsuleStream to read one stream now.
   */
  resumeCapsuleStream(hostId: string, capsuleId: string, streamName: string) {
    return apiClient.post<void>(
      `${this.base(hostId)}/capsules/${capsuleId}/streams/${encodeURIComponent(streamName)}/resume`
    );
  }

  /**
   * Replace a source's stream configuration atomically on Edge.
   *
   * Phase 2.1 of capsule-ingestion-restructuring. Writes land in
   * `source_registry.streams` JSONB + per-stream `sync_state.sync_interval`
   * inside one transaction on the ingesting host. Missing streams are dropped
   * from the registry (their `sync_state` rows are left for ops recovery).
   * Replaces the old Platform-side path that wrote only to
   * governance_resources.resource_data and never reached Edge.
   */
  updateSourceStreams(
    hostId: string,
    capsuleId: string,
    sourceId: string,
    data: UpdateSourceStreamsRequest
  ) {
    return apiClient.put<UpdateSourceStreamsResponse>(
      `${this.base(hostId)}/capsules/${capsuleId}/sources/${sourceId}/streams`,
      data
    );
  }

  // --- Documents ---

  listDocuments(hostId: string, capsuleId: string, params?: Record<string, string>) {
    return apiClient.get<{
      documents: DocumentSummary[];
      total: number;
      page: number;
      perPage: number;
    }>(`${this.base(hostId)}/capsules/${capsuleId}/documents`, params);
  }

  getDocument(hostId: string, documentId: string) {
    return apiClient.get<DocumentDetail>(`${this.base(hostId)}/documents/${documentId}`);
  }

  deleteDocument(hostId: string, capsuleId: string, externalId: string) {
    return apiClient.delete<void>(
      `${this.base(hostId)}/capsules/${capsuleId}/documents/${encodeURIComponent(externalId)}`
    );
  }

  // --- Taxonomy ---

  listTaxonomy(hostId: string, capsuleId: string) {
    return apiClient.get<TaxonomyNode[]>(`${this.base(hostId)}/capsules/${capsuleId}/taxonomy`);
  }

  createTerm(hostId: string, capsuleId: string, data: CreateTermRequest) {
    return apiClient.post<{ id: string }>(
      `${this.base(hostId)}/capsules/${capsuleId}/taxonomy`,
      data
    );
  }

  updateTerm(
    hostId: string,
    termId: string,
    data: {
      label?: string;
      synonyms?: string[];
      language?: string;
      translations?: Record<string, string[]>;
    }
  ) {
    return apiClient.put<{ updated: boolean }>(`${this.base(hostId)}/taxonomy/${termId}`, data);
  }

  deleteTerm(hostId: string, termId: string) {
    return apiClient.delete<{ deleted: boolean }>(`${this.base(hostId)}/taxonomy/${termId}`);
  }

  previewSynonyms(
    hostId: string,
    capsuleId: string,
    data: { query: string; newSynonyms: string[]; termLabel: string }
  ) {
    return apiClient.post<PreviewResponse>(
      `${this.base(hostId)}/capsules/${capsuleId}/taxonomy/preview`,
      data
    );
  }

  // --- Annotations ---

  createAnnotation(
    hostId: string,
    data: {
      documentId: string;
      chunkId?: string;
      capsuleId?: string;
      annotationText: string;
      annotationType: string;
      authorId: string;
    }
  ) {
    return apiClient.post<AnnotationResponse>(`${this.base(hostId)}/annotations`, data);
  }

  listAnnotations(hostId: string, capsuleId: string) {
    return apiClient.get<AnnotationResponse[]>(
      `${this.base(hostId)}/capsules/${capsuleId}/annotations`
    );
  }

  updateAnnotation(hostId: string, annotationId: string, data: { annotationText: string }) {
    return apiClient.put<AnnotationResponse>(
      `${this.base(hostId)}/annotations/${annotationId}`,
      data
    );
  }

  deleteAnnotation(hostId: string, annotationId: string) {
    return apiClient.delete(`${this.base(hostId)}/annotations/${annotationId}`);
  }

  // --- Overrides ---

  createOverride(
    hostId: string,
    data: {
      capsuleId?: string;
      documentId?: string;
      overrideType: string;
      queryPattern?: string;
      overrideValue?: unknown;
      reason: string;
      authorId: string;
      expiresAt?: string;
    }
  ) {
    return apiClient.post<OverrideResponse>(`${this.base(hostId)}/overrides`, data);
  }

  listOverrides(hostId: string, capsuleId: string) {
    return apiClient.get<OverrideResponse[]>(
      `${this.base(hostId)}/capsules/${capsuleId}/overrides`
    );
  }

  deactivateOverride(hostId: string, overrideId: string) {
    return apiClient.put<void>(`${this.base(hostId)}/overrides/${overrideId}/deactivate`, {});
  }

  // --- Quality ---

  getQualityOverview(hostId: string) {
    return apiClient.get<QualityOverview>(`${this.base(hostId)}/quality/overview`);
  }

  getCapsuleQuality(hostId: string, capsuleId: string) {
    return apiClient.get<CapsuleQualityDetail>(
      `${this.base(hostId)}/capsules/${capsuleId}/quality`
    );
  }

  getStaleDocuments(hostId: string, capsuleId: string) {
    return apiClient.get<StaleDocument[]>(
      `${this.base(hostId)}/capsules/${capsuleId}/quality/stale`
    );
  }

  // --- Assembly Inspector ---

  listAssemblies(hostId: string, params?: Record<string, string>) {
    return apiClient.get<{
      assemblies: AssemblySummary[];
      total: number;
      page: number;
      perPage: number;
    }>(`${this.base(hostId)}/assemblies`, params);
  }

  getAssembly(hostId: string, compositionId: string) {
    return apiClient.get<AssemblyTrace>(`${this.base(hostId)}/assemblies/${compositionId}`);
  }

  recordOutcome(
    hostId: string,
    data: { compositionId: string; outcome: string; feedback?: string; selectedCapsules?: string[] }
  ) {
    return apiClient.post<{ id: string }>(`${this.base(hostId)}/outcomes`, data);
  }
  // Ingestion
  async ingest(hostId: string, capsuleId: string, data: IngestRequest): Promise<IngestResponse> {
    return apiClient.post<IngestResponse>(
      `${this.base(hostId)}/capsules/${capsuleId}/ingest`,
      data
    );
  }

  // Knowledge Graph
  async graphTraverse(
    hostId: string,
    params: { startNodeId: string; maxHops?: number; edgeTypes?: string }
  ): Promise<GraphTraverseResponse> {
    const searchParams: Record<string, string> = { start_node_id: params.startNodeId };
    if (params.maxHops !== undefined) searchParams['max_hops'] = String(params.maxHops);
    if (params.edgeTypes) searchParams['edge_types'] = params.edgeTypes;
    return apiClient.get<GraphTraverseResponse>(
      `${this.base(hostId)}/graph/traverse`,
      searchParams
    );
  }

  // Embeddings
  async batchEmbed(hostId: string, texts: string[]): Promise<EmbedResponse> {
    return apiClient.post<EmbedResponse>(`${this.base(hostId)}/embed`, { texts });
  }

  // --- Structured Data ---

  async getCapsuleSchema(hostId: string, capsuleId: string) {
    const capsule = await this.getCapsule(hostId, capsuleId);
    return legacyCapsuleSurfacesEnabled(capsule)
      ? this.getLegacyCapsuleSchema(hostId, capsuleId)
      : capsuleSchemaFromDetail(capsule);
  }

  getLegacyCapsuleSchema(hostId: string, capsuleId: string) {
    // Raw: column names are user-defined, must not be camelCased
    return apiClient.getRaw<CapsuleSchemaResponse>(
      `${this.base(hostId)}/capsules/${capsuleId}/schema`
    );
  }

  queryData(hostId: string, capsuleId: string, request: DataQueryRequest) {
    // Raw: row keys are DB column names (snake_case), must not be camelCased
    return apiClient.postRaw<DataQueryResponse>(
      `${this.base(hostId)}/capsules/${capsuleId}/data/query`,
      request
    );
  }

  // --- Universal Search: mission-design-binding ---

  searchBindings(hostId: string, params: UniversalBindingSearchParams) {
    return apiClient.post<UniversalBindingSearchResponse>(`${this.base(hostId)}/universal/search`, {
      apiVersion: '1',
      query: params.query,
      queryMode: 'mission-design-binding',
      scope: {
        hostId,
        entityTypes: params.entityTypes ?? ['governance_resource', 'platform_tool', 'data_capsule'],
        includePlatformCatalog: true,
      },
      persona: {
        userId: 'admin-ui',
        authzMode: 'human_role',
        role: 'admin',
      },
      context: {
        nodeType: params.nodeType,
        nodeLabel: params.nodeLabel,
        nodeDescription: params.nodeDescription,
        missionType: params.missionType,
        executionMode: params.executionMode,
        connectedAgentLabel: params.connectedAgentLabel,
        existingBindings: params.existingBindings,
      },
      budget: {
        maxResults: params.maxResults ?? 20,
        maxLatencyMs: 2000,
      },
      format: 'ranked',
    });
  }

  // --- Intelligence Audit Log (plan: classifier-llm-assist.md Phase 3) ---

  listAuditEvents(hostId: string, query: AuditEventsQuery = {}, options?: GetRequestOptions) {
    const params = new URLSearchParams();
    if (query.capsuleId) params.set('capsule_id', query.capsuleId);
    if (query.eventType) params.set('event_type', query.eventType);
    if (query.from) params.set('from', query.from);
    if (query.to) params.set('to', query.to);
    if (query.limit != null) params.set('limit', String(query.limit));
    // L2 free-text search (V106 + capsule_audit_events.search_vector).
    // Empty/whitespace-only is dropped client-side so we don't waste a
    // round-trip when the operator clears the input. The server treats
    // an empty `q` as "no filter" too; this is just belt-and-braces.
    const qText = query.q?.trim();
    if (qText) params.set('q', qText);
    const qs = params.toString();
    const path = qs
      ? `${this.base(hostId)}/audit-events?${qs}`
      : `${this.base(hostId)}/audit-events`;
    return apiClient.get<AuditEventsResponse>(path, undefined, options);
  }

  listEventSpine(hostId: string, query: AuditTimelineQuery = {}, options?: GetRequestOptions) {
    const params = new URLSearchParams();
    if (query.traceId) params.set('trace_id', query.traceId);
    if (query.missionId) params.set('mission_id', query.missionId);
    if (query.eventType) params.set('event_type', query.eventType);
    if (query.sourceSystem) params.set('source_system', query.sourceSystem);
    if (query.learningEligible != null)
      params.set('learning_eligible', String(query.learningEligible));
    if (query.limit != null) params.set('limit', String(query.limit));
    const qText = query.q?.trim();
    if (qText) params.set('q', qText);
    const qs = params.toString();
    const path = qs ? `${this.base(hostId)}/event-spine?${qs}` : `${this.base(hostId)}/event-spine`;
    return apiClient.get<AuditTimelineResponse>(path, undefined, options);
  }

  // --- Agent overlays (Phase 6 audit-page wiring) ---

  /** Fetches the currently published overlay rows for a capsule —
   *  proposed + promoted, latest-supersession winners only. The audit
   *  page renders these as the overlay timeline. */
  listPublishedOverlays(hostId: string, capsuleId: string, options?: GetRequestOptions) {
    return apiClient.get<AgentOverlayPublishedEvidence[]>(
      `${this.base(hostId)}/capsules/${encodeURIComponent(capsuleId)}/agent-overlays/published`,
      undefined,
      options
    );
  }

  /** Creates the P06 write_attempt journal event the override endpoint
   *  requires. The audit page calls this just before submitting an
   *  override so each override is anchored to a fresh journal row. */
  createOverlayWriteAttemptJournal(hostId: string, body: CreateOverlayWriteAttemptJournalRequest) {
    return apiClient.post<CreateOverlayWriteAttemptJournalResponse>(
      `${this.base(hostId)}/agent-overlays/journal/write-attempt`,
      body
    );
  }

  /** Submits a Change-decision request — server re-runs the safety
   *  gates that protect against ungrounded promotions and either
   *  appends a derived `human_override` row or returns a structured
   *  rejection naming the gate that failed. */
  overrideOverlayDecision(
    hostId: string,
    overlayId: string,
    body: AgentOverlayDecisionOverrideRequest
  ) {
    return apiClient.post<AgentOverlayDecisionOverrideResponse>(
      `${this.base(hostId)}/agent-overlays/${encodeURIComponent(overlayId)}/decision-override`,
      body
    );
  }

  // --- Capsule Refresh / Re-enrichment (E5-S3) ---

  /**
   * Fetch refresh cost estimate for a capsule. Returns estimated
   * processing costs broken down by enrichment category.
   */
  getRefreshEstimate(hostId: string, capsuleId: string) {
    return apiClient.get<RefreshEstimateResponse>(
      `${this.base(hostId)}/capsules/${encodeURIComponent(capsuleId)}/refresh-estimate`
    );
  }

  /**
   * Trigger a delta-only refresh. Only reprocesses rows changed since
   * the last enrichment run.
   */
  postRefresh(hostId: string, capsuleId: string) {
    return apiClient.post<RefreshResponse>(
      `${this.base(hostId)}/capsules/${encodeURIComponent(capsuleId)}/refresh`,
      { mode: 'delta' }
    );
  }

  /**
   * Trigger a full re-enrichment. Reprocesses all rows. Requires
   * name confirmation to prevent accidental costly operations.
   */
  postFullRefresh(hostId: string, capsuleId: string, confirmationName: string) {
    return apiClient.post<RefreshResponse>(
      `${this.base(hostId)}/capsules/${encodeURIComponent(capsuleId)}/full-refresh`,
      { mode: 'full', confirmationName }
    );
  }

  /**
   * Record data rows into a capsule stream. Max 200 rows per call.
   * Uses postRaw to preserve snake_case keys (stream_name, dedupe_keys)
   * and arbitrary user-defined column names in the row payloads.
   */
  recordCapsuleData(hostId: string, capsuleId: string, body: RecordCapsuleDataRequest) {
    return apiClient.postRaw<RecordCapsuleDataResponse>(
      `${this.base(hostId)}/capsules/${encodeURIComponent(capsuleId)}/data/records`,
      body
    );
  }
}

// --- Structured Data types ---

export interface CapsuleSchemaResponse {
  capsule_id: string;
  version: string;
  json_schema: Record<string, unknown>;
  tables: TableInfo[];
}

export interface DocumentArtifactReceipt {
  stage: string;
  attempt: number;
  status: 'running' | 'succeeded' | 'failed' | 'skipped';
  /** Why the step failed or was skipped: the worker's failurereason vocabulary. */
  reasonCode?: string;
  // Read only when reasonCode is absent: a worker older than
  // agion/Agion-Intelligence#207 sends error_code (and an error_message the
  // interface never shows, since it can carry a source URL or document text).
  errorCode?: string;
  startedAt: string;
  completedAt?: string;
}

export interface DocumentArtifactItem {
  id: string;
  filename: string;
  mediaType: string;
  byteSize: number;
  originKind: 'manual_upload' | 'connector';
  retentionMode: 'host_retained' | 'source_linked';
  hostId?: string;
  hostFileId?: string;
  sourceUri?: string;
  artifactState: 'active' | 'superseded' | 'deleted';
  createdAt: string;
  updatedAt: string;
  revision: {
    id: string;
    status:
      | 'planned'
      | 'running'
      | 'searchable'
      | 'enriching'
      | 'completed'
      | 'degraded'
      | 'failed'
      | 'superseded';
    parserName: string;
    parserVersion: string;
    searchableAt?: string;
    completedAt?: string;
  };
  receipts: DocumentArtifactReceipt[];
}

export interface DocumentArtifactListResponse {
  capsuleId: string;
  artifacts: DocumentArtifactItem[];
}

/** A document the capsule read but could not make searchable. */
export interface NotIngestedDocument {
  artifactId: string;
  displayName: string;
  mediaType: string;
  originKind: 'manual_upload' | 'connector';
  /** The document's page in the source system (connector documents, https only). */
  sourceUri?: string;
  /** skipped: read, but nothing to search (no text); failed: an error stopped it. */
  outcome: 'skipped' | 'failed';
  /** Bounded code: no_extractable_text, parser_failed, chunk_failed, publish_failed, receipt_failed, other, unknown, or a newer producer code. */
  reasonCode: string;
  /** The processing step that ended it: parse, chunk, publish, ... */
  stage?: string;
  attemptedAt: string;
}

/** One reason's count from the latest walk of every stream; the code is a worker failure-reason code. */
export interface NotIngestedReasonCount {
  outcome: 'skipped' | 'failed';
  reasonCode: string;
  count: number;
}

export interface NotIngestedMediaTypeCount {
  /** A media type, or "other" for the folded tail. */
  mediaType: string;
  count: number;
}

/** A SharePoint file whose read timed out attempt_limit times on the same content. */
export interface StoppedAfterTimeoutsFile {
  displayName: string;
  /** The file's page in SharePoint (https only); absent when the worker has none. */
  sourceUri?: string;
  attempts: number;
  lastAttemptedAt: string;
}

/**
 * The files the worker stopped trying after repeated read timeouts, from its
 * timeout memory. Absent on a worker that predates the block.
 */
export interface StoppedAfterTimeouts {
  /** Every stopped file, also those beyond the listed ones. */
  count: number;
  /** Timeouts after which a file is stopped. */
  attemptLimit: number;
  /** How long one read may take, in seconds. */
  parseTimeLimitSeconds: number;
  /** The server's verdict on whether one more try can be granted now. */
  canRetry: boolean;
  /** At most 100 files. */
  files: StoppedAfterTimeoutsFile[];
  /** More files are stopped than listed. */
  filesTruncated: boolean;
}

/** Counts for documents that never became a row (unsupported type, download or reader failure). First page only. */
export interface NotIngestedSummary {
  byReason: NotIngestedReasonCount[];
  skippedMediaTypes: NotIngestedMediaTypeCount[];
  stoppedAfterTimeouts?: StoppedAfterTimeouts;
}

/** The answer to retryTimedOutDocuments. */
export interface TimedOutRetryResponse {
  /** Files that get one more try; 0 when none was stopped. */
  granted: number;
  parseTimeLimitSeconds: number;
  /** The read request the worker queued; absent when nothing was granted. */
  manualTriggerId?: string;
}

export interface NotIngestedDocumentsPage {
  capsuleId: string;
  documents: NotIngestedDocument[];
  total: number;
  nextCursor?: string;
  summary?: NotIngestedSummary;
}

export interface DocumentArtifactTriggerResponse {
  status: string;
  capsuleId: string;
  artifactId: string;
  processingRevisionId: string;
}

export interface TableInfo {
  name: string;
  /** Human-facing stream name. Internal names may be namespaced to avoid
   * collisions between connector streams and uploaded workbook sheets. */
  display_name?: string;
  /** Human-facing ingestion provenance, such as a connector kind or filename. */
  source_label?: string;
  /** ID of the source_registry row that owns this table (Slice D1).
   *  Optional because tables whose owning source was removed, or
   *  pre-association legacy rows, have no current mapping. */
  source_id?: string;
  columns: ColumnInfo[];
}

export interface ColumnInfo {
  name: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  data_type: string;
  nullable: boolean;
  /**
   * Whether a value must be supplied when inserting a row: the column is
   * NOT NULL, has no DEFAULT, and is neither identity nor generated.
   * intelligence-zone computes this with the same predicate the record-insert
   * path enforces. Optional — older IZ hosts omit it, in which case callers
   * fall back to the `!nullable` heuristic. Lets the CSV seeder treat a
   * NOT NULL column with a DEFAULT (e.g. visit_count) as optional to map.
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  required_on_insert?: boolean;
  /**
   * Per-column semantic role from V036's field_classification_decisions
   * in intelligence-zone, with operator overrides winning. One of:
   * PII | KEY | REF | CURSOR | TIME | TITLE | EMBED | FACT | ASSET | SKIP.
   * Optional — older IZ responses and capsules without classifier output
   * yet omit it. Mission Designer renders human labels ("tunniste",
   * "aikadimensio") instead of just the raw type.
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  classifier_role?: ClassifierRole;
}

/** The 10 semantic roles intelligence-zone's classifier emits, plus
 *  operator overrides via `user_role`. Order locked by V036's CHECK constraint. */
export type ClassifierRole =
  | 'PII'
  | 'KEY'
  | 'REF'
  | 'CURSOR'
  | 'TIME'
  | 'TITLE'
  | 'EMBED'
  | 'FACT'
  | 'ASSET'
  | 'SKIP';

export interface DataQueryRequest {
  stream_name: string;
  columns?: string[];
  filters?: DataFilter[];
  order_by?: string;
  limit?: number;
  offset?: number;
}

export interface DataFilter {
  column: string;
  op: string;
  value: unknown;
}

export interface DataQueryResponse {
  rows: Record<string, unknown>[];
  /** Rows returned in this page. */
  count: number;
  columns: string[];
  /** Total rows matching the filters (ignoring limit/offset). Absent on aggregate responses. */
  total?: number;
}

// --- Record Data types (CSV seed) ---

/** Request body for POST /capsules/{id}/data/records. Snake_case keys are
 *  required by the edge contract; apiClient.postRaw preserves them. */
export interface RecordCapsuleDataRequest {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  stream_name: string;
  rows: Record<string, unknown>[];
  // eslint-disable-next-line @typescript-eslint/naming-convention
  dedupe_keys?: string[];
}

export interface RecordCapsuleDataResponse {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  capsule_id: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  stream_name: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  rows_recorded: number;
  inserted: number;
  updated: number;
  results: Array<{
    // eslint-disable-next-line @typescript-eslint/naming-convention
    row_id: string;
    created: boolean;
  }>;
}

export const intelligenceService = new IntelligenceService();
